/**
 * Observability транскодера переговорок.
 *
 * Корневая сага ставит monkey-patch на конструктор `RTCPeerConnection` и
 * регистрирует обработчики goloom-событий через локальный ChannelHolder,
 * который закрывается в finally.
 */
import { Participant } from '@yandex-video-platform/goloom-sdk';
import { all, call } from 'typed-redux-saga';

import { goloomSdk } from '../../../../../shared/features/goloom-sdk';
import { ChannelHolder } from '../../../../../shared/helpers/channelHolder';
import { ACTION, FEATURE } from '../../../../../shared/helpers/logger-enums';
import { createLogger } from '../../../../../shared/helpers/loggerFacade';
import { PUBLISH_EVERY_N_TICKS, STATS_TICK_MS } from './constants';
import { collectTranscoderInboundSample } from './helpers/collect-transcoder-inbound-stats';
import { FreezeDetector } from './helpers/freeze-detector';
import { isTranscoderParticipant } from './helpers/is-transcoder-participant';
import { patchRTCPeerConnection } from './helpers/peer-connection-registry';
import { getTranscoderVideoTracks } from './helpers/transcoder-track-registry';
import type { TranscoderInboundSample } from './types';

const logger = createLogger('transcoder-observability');

type PeerBitrateState = {
    bytesReceived: number;
    framesDecoded: number;
    timestampMs: number;
};

const freezeDetector = new FreezeDetector();
const prevSamples = new Map<string, PeerBitrateState>();

// Единый mutable state тика — таймер, счётчик, флаг занятости.
// `running` защищает от гонки: если предыдущий тик не успел закончить getStats
// за STATS_TICK_MS, следующий setInterval-прогон пропускаем.
const tickState: {
    timer: ReturnType<typeof setInterval> | undefined;
    count: number;
    running: boolean;
} = {
    timer: undefined,
    count: 0,
    running: false,
};

function getCurrentTranscoderParticipants(): Participant[] {
    return goloomSdk.participants.filter(isTranscoderParticipant);
}

function publishGauges(peerId: string, bitrateBps: number, fps: number, participantsCount: number): void {
    const additional = { transcoder_peer_id: peerId, participants_count: participantsCount };
    const emit = (action: ACTION, value: number) => {
        logger.featureEventInteger({
            feature: FEATURE.CONFERENCE,
            action,
            value: Math.max(0, Math.round(value)),
            isSuccess: true,
            options: { additional },
        });
    };

    emit(ACTION.TRANSCODER_VIDEO_BITRATE, bitrateBps);
    emit(ACTION.TRANSCODER_VIDEO_FPS, fps);
}

// Каждый тик кормим freeze-детектор свежими framesDecoded и sendVideo.
// Раз в PUBLISH_EVERY_N_TICKS считаем дельту по bytesReceived / framesDecoded и публикуем bitrate + FPS.
function processSample(
    peerId: string,
    sample: TranscoderInboundSample,
    sendVideo: boolean,
    shouldPublish: boolean,
    participantsCount: number
): void {
    const now = Date.now();
    freezeDetector.tick({
        peerId,
        framesDecoded: sample.framesDecoded,
        sendVideo,
        nowMs: now,
        participantsCount,
    });

    const prev = prevSamples.get(peerId);
    if (shouldPublish && prev) {
        const intervalSec = (sample.timestampMs - prev.timestampMs) / 1000;
        if (intervalSec > 0) {
            const bitrateBps = ((sample.bytesReceived - prev.bytesReceived) * 8) / intervalSec;
            const framesDelta = sample.framesDecoded - prev.framesDecoded;
            const derivedFps = framesDelta > 0 ? framesDelta / intervalSec : 0;
            publishGauges(peerId, bitrateBps, sample.framesPerSecond ?? derivedFps, participantsCount);
        }
    }

    // Обновляем baseline только по факту публикации (или если baseline'а ещё нет),
    // чтобы дельта считалась за полный PUBLISH_EVERY_N_TICKS-интервал, а не один тик.
    if (shouldPublish || !prev) {
        prevSamples.set(peerId, {
            bytesReceived: sample.bytesReceived,
            framesDecoded: sample.framesDecoded,
            timestampMs: sample.timestampMs,
        });
    }
}

async function runStatsTick(): Promise<void> {
    const transcoderParticipants = getCurrentTranscoderParticipants();
    if (transcoderParticipants.length === 0) {
        return;
    }

    tickState.count += 1;
    const shouldPublish = tickState.count % PUBLISH_EVERY_N_TICKS === 0;
    const participantsCount = goloomSdk.participants.length;

    const trackByPeer = new Map<string, MediaStreamTrack>();
    for (const entry of getTranscoderVideoTracks()) {
        trackByPeer.set(entry.peerId, entry.track);
    }

    await Promise.all(
        transcoderParticipants.map(async (participant) => {
            const track = trackByPeer.get(participant.userId);
            // Нет слота = участник вне видимой страницы grid'а → не меряем.
            if (!track) {
                return;
            }
            try {
                const sample = await collectTranscoderInboundSample(participant.userId, track);
                if (!sample) {
                    return;
                }
                processSample(
                    participant.userId,
                    sample,
                    Boolean(participant.sendVideo),
                    shouldPublish,
                    participantsCount
                );
            } catch (error) {
                logger.warn(error instanceof Error ? error : new Error(String(error)), {
                    method: 'runStatsTick',
                    additional: { transcoder_peer_id: participant.userId },
                });
            }
        })
    );
}

function scheduleStatsTick(): void {
    if (tickState.running) {
        return;
    }
    tickState.running = true;
    runStatsTick()
        .catch((error) => {
            logger.warn(error instanceof Error ? error : new Error(String(error)), {
                method: 'scheduleStatsTick',
            });
        })
        .finally(() => {
            tickState.running = false;
        });
}

function ensureTimerRunning(): void {
    if (tickState.timer !== undefined) {
        return;
    }
    tickState.timer = setInterval(scheduleStatsTick, STATS_TICK_MS);
}

function stopTimer(): void {
    if (tickState.timer !== undefined) {
        clearInterval(tickState.timer);
        tickState.timer = undefined;
    }
    tickState.count = 0;
    tickState.running = false;
}

function anyTranscoderPresent(): boolean {
    return goloomSdk.participants.some(isTranscoderParticipant);
}

function fullReset(): void {
    stopTimer();
    freezeDetector.clear();
    prevSamples.clear();
}

function onTranscoderParticipantsAdded(participants: Participant[]): void {
    if (participants.some(isTranscoderParticipant)) {
        ensureTimerRunning();
    }
}

function onTranscoderParticipantsRemoved(participants: Participant[]): void {
    for (const participant of participants) {
        if (isTranscoderParticipant(participant)) {
            freezeDetector.drop(participant.userId);
            prevSamples.delete(participant.userId);
        }
    }
    if (!anyTranscoderPresent()) {
        stopTimer();
    }
}

function onGoloomDestroy(): void {
    fullReset();
}

export function* transcoderObservabilityRootSaga() {
    yield* call(patchRTCPeerConnection);

    const channelHolder = new ChannelHolder();
    try {
        yield* all([
            channelHolder.takeEvery(goloomSdk.onParticipantsAdded, onTranscoderParticipantsAdded),
            channelHolder.takeEvery(goloomSdk.onParticipantsRemoved, onTranscoderParticipantsRemoved),
            channelHolder.takeEvery(goloomSdk.onDestroy, onGoloomDestroy),
        ]);
    } finally {
        channelHolder.close();
        fullReset();
    }
}
